/** Energy and macros from per-100 g figures. */
import type { Food, MacroTotals, Nutrition } from './types'
import { kjToKcal, round } from './units'

const KCAL_PER_G = { protein: 4, fat: 9, carb: 4 }

function finite(n: number | undefined): number {
  return n !== undefined && Number.isFinite(n) ? n : 0
}

/**
 * kcal per 100 g: the stated kcal, else the label's kJ, else Atwater from macros, else 0.
 * Never NaN or negative, so one half-entered food cannot poison a total. A stated 0 is a
 * figure, not a gap: erythritol lists carbohydrate that yields no energy.
 */
export function energyPer100g(food: Pick<Food, 'kcal_per_100g' | 'kj_per_100g' | 'nutrition'>) {
  if (Number.isFinite(food.kcal_per_100g) && food.kcal_per_100g >= 0) return food.kcal_per_100g
  if (food.kj_per_100g !== undefined && Number.isFinite(food.kj_per_100g) && food.kj_per_100g > 0) {
    return kjToKcal(food.kj_per_100g)
  }
  const n = food.nutrition
  if (n) {
    const kcal =
      finite(n.protein_g) * KCAL_PER_G.protein +
      finite(n.fat_g) * KCAL_PER_G.fat +
      finite(n.carb_g) * KCAL_PER_G.carb
    if (kcal > 0) return kcal
  }
  return 0
}

export function kcalForGrams(food: Food, grams: number): number {
  return (energyPer100g(food) * finite(grams)) / 100
}

const NUTRIENT_KEYS: (keyof Nutrition)[] = [
  'protein_g',
  'fat_g',
  'saturated_fat_g',
  'carb_g',
  'sugar_g',
  'fibre_g',
  'sodium_mg',
  'potassium_mg',
]

/** Absolute nutrients in `grams` of the food; undefined when the food has no nutrition. */
export function nutritionForGrams(food: Food, grams: number): Nutrition | undefined {
  return scaleNutrition(food.nutrition, finite(grams) / 100)
}

export function scaleNutrition(n: Nutrition | undefined, factor: number): Nutrition | undefined {
  if (!n) return undefined
  const out: Nutrition = {}
  for (const k of NUTRIENT_KEYS) {
    const v = n[k]
    if (v !== undefined && Number.isFinite(v)) out[k] = v * factor
  }
  return out
}

/** True when a nutrition record has the three energy macros a share needs. */
export function hasMacros(n: Nutrition | undefined): boolean {
  return (
    n !== undefined && n.protein_g !== undefined && n.fat_g !== undefined && n.carb_g !== undefined
  )
}

/** Share of energy from protein / fat / carb at 4/9/4 kcal per g; undefined with no energy. */
export function macroShare(m: Pick<Nutrition, 'protein_g' | 'fat_g' | 'carb_g'>) {
  const p = finite(m.protein_g) * KCAL_PER_G.protein
  const f = finite(m.fat_g) * KCAL_PER_G.fat
  const c = finite(m.carb_g) * KCAL_PER_G.carb
  const total = p + f + c
  if (total <= 0) return undefined
  return { protein: round(p / total, 4), fat: round(f / total, 4), carb: round(c / total, 4) }
}

/** One thing to add up: its absolute nutrients (if known) and its energy. */
export interface MacroPart {
  macros?: Nutrition
  kcal: number
}

/**
 * Add up absolute nutrients. `partial` is set when something with energy had no macros, so
 * the totals are a floor rather than the truth.
 */
export function sumMacros(parts: readonly MacroPart[]): MacroTotals {
  const t = { protein_g: 0, fat_g: 0, carb_g: 0, fibre_g: 0, sugar_g: 0, sodium_mg: 0 }
  let partial = false
  for (const part of parts) {
    if (!hasMacros(part.macros)) {
      if (part.kcal > 0) partial = true
      if (!part.macros) continue
    }
    const m = part.macros as Nutrition
    t.protein_g += finite(m.protein_g)
    t.fat_g += finite(m.fat_g)
    t.carb_g += finite(m.carb_g)
    t.fibre_g += finite(m.fibre_g)
    t.sugar_g += finite(m.sugar_g)
    t.sodium_mg += finite(m.sodium_mg)
  }
  const out: MacroTotals = {
    protein_g: round(t.protein_g, 1),
    fat_g: round(t.fat_g, 1),
    carb_g: round(t.carb_g, 1),
    fibre_g: round(t.fibre_g, 1),
    sugar_g: round(t.sugar_g, 1),
    sodium_mg: round(t.sodium_mg, 0),
    partial,
  }
  const share = macroShare(t)
  if (share) out.energy_share = share
  return out
}
